import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  CreditCard, Check, Zap, Building2, Crown, AlertCircle, FileText, Download, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_CARDS = [
  { tier: "starter",      icon: Check,    accent: "from-sky-400 to-indigo-500",      benefits: ["1 site", "2 users", "3 modules max", "Email support"] },
  { tier: "professional", icon: Zap,      accent: "from-orange-500 to-pink-500",     benefits: ["3 sites", "10 users per site", "All modules", "Priority support"] },
  { tier: "enterprise",   icon: Crown,    accent: "from-purple-500 to-fuchsia-500",  benefits: ["Unlimited sites & users", "All modules", "Custom branding", "24×7 phone support", "Dedicated account manager"] },
];

const CATEGORY_LABELS = {
  monitoring: "Monitoring Modules",
  registry: "Asset Registry Modules",
  management: "Management Modules",
  operations: "Operations Modules",
  admin: "Administration (Core)",
};

const CATEGORY_ICONS = {
  monitoring: Building2,
  registry: Check,
  management: Zap,
  operations: AlertCircle,
  admin: Crown,
};

export default function Billing() {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [saving, setSaving] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [payModal, setPayModal] = useState(null);  // selected plan_tier
  const [cancelModal, setCancelModal] = useState(false);

  const load = async () => {
    try {
      const [s, inv, pl] = await Promise.all([
        api.get("/billing/my-subscription"),
        api.get("/billing/invoices"),
        api.get("/billing/plans"),
      ]);
      setSub(s.data);
      setInvoices(inv.data);
      setPlans(pl.data);
    } catch (e) {
      toast.error("Failed to load billing data");
    }
  };

  useEffect(() => { load(); }, []);

  const toggleModule = async (module_key, is_enabled) => {
    setBusyKey(module_key);
    try {
      await api.put(`/billing/modules/${module_key}`, { is_enabled });
      await load();
      toast.success(`${module_key} ${is_enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusyKey(null);
    }
  };

  const updateUnits = async (module_key, unit_count) => {
    const n = Math.max(0, parseInt(unit_count, 10) || 0);
    setBusyKey(module_key);
    try {
      await api.put(`/billing/modules/${module_key}`, { unit_count: n });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusyKey(null);
    }
  };

  const generateInvoice = async () => {
    setSaving(true);
    try {
      await api.post("/billing/invoices/generate");
      toast.success("Invoice generated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    } finally {
      setSaving(false);
    }
  };

  const grouped = useMemo(() => {
    if (!sub?.modules) return {};
    return sub.modules.reduce((acc, m) => {
      const c = m.module_category;
      (acc[c] = acc[c] || []).push(m);
      return acc;
    }, {});
  }, [sub]);

  if (!sub) return <div className="text-gray-500">Loading billing…</div>;

  const s = sub.summary;
  const plan = sub.plan || {};
  const isAnnual = cycle === "annual";
  const currentTier = plan.plan_tier;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Billing & Plan</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your subscription, modules, and invoices</p>
      </div>

      {/* PLAN CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plan-cards">
        {PLAN_CARDS.map((p) => {
          const def = plans.find((x) => x.plan_tier === p.tier) || {};
          const Icon = p.icon;
          const isCurrent = currentTier === p.tier;
          const price = isAnnual ? def.base_price_annual : def.base_price_monthly;
          return (
            <div key={p.tier} className={`glass rounded-lg p-6 relative overflow-hidden flex flex-col ${isCurrent ? "ring-2 ring-orange-500/50" : ""}`} data-testid={`plan-card-${p.tier}`}>
              {isCurrent && <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-orange-500/30 text-orange-700 font-bold">Current Plan</span>}
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${p.accent} flex items-center justify-center shadow-lg mb-3`}>
                <Icon className="w-6 h-6 text-gray-900" />
              </div>
              <h3 className="text-xl font-bold capitalize">{def.name || p.tier}</h3>
              <div className="mt-2 mb-3">
                <span className="text-3xl font-extrabold">₹{(price || 0).toLocaleString("en-IN")}</span>
                <span className="text-gray-500 text-sm">/{isAnnual ? "yr" : "mo"}</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">{def.description}</p>
              <ul className="space-y-1.5 text-sm flex-1">
                {p.benefits.map((b) => <li key={b} className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2.5} /> {b}</li>)}
              </ul>
              <Button
                onClick={() => !isCurrent && setPayModal({ tier: p.tier, name: def.name, amount: price })}
                disabled={isCurrent}
                className={`mt-4 ${isCurrent ? "bg-gray-100 text-gray-500" : "f360-gradient"} border-0 w-full`}
                data-testid={`plan-cta-${p.tier}`}>
                {isCurrent ? "Active" : currentTier === "starter" || (currentTier === "professional" && p.tier === "enterprise") ? "Upgrade" : currentTier === "professional" && p.tier === "starter" ? "Downgrade" : currentTier === "enterprise" ? "Downgrade" : "Subscribe Now"}
              </Button>
            </div>
          );
        })}
      </div>

      {/* CURRENT PLAN */}
      <div className="glass p-6 rounded-lg" data-testid="current-plan-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Current Plan</div>
            <div className="flex items-center gap-3 mt-1">
              <div className="text-2xl font-extrabold">{plan.name || "—"}</div>
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest f360-gradient text-white font-bold">{plan.plan_tier}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${sub.subscription?.status === "active" ? "bg-emerald-500/20 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`}>{sub.subscription?.status || "inactive"}</span>
            </div>
            <div className="text-xs text-gray-500 mt-2">Next billing: <span className="text-gray-900">{sub.subscription?.next_billing_date ? new Date(sub.subscription.next_billing_date).toLocaleDateString() : "—"}</span></div>
          </div>

          <div className="flex items-center gap-2">
            <div className="bg-gray-50 rounded-lg p-1 flex items-center text-xs">
              <button data-testid="cycle-monthly" onClick={() => setCycle("monthly")} className={`px-3 py-1.5 rounded ${!isAnnual ? "f360-gradient text-white" : "text-gray-700"}`}>Monthly</button>
              <button data-testid="cycle-annual" onClick={() => setCycle("annual")} className={`px-3 py-1.5 rounded ${isAnnual ? "f360-gradient text-white" : "text-gray-700"}`}>Annual (2 mo free)</button>
            </div>
            <Button className="f360-gradient border-0" data-testid="upgrade-plan-btn" onClick={() => setPayModal({ tier: currentTier || "professional", name: plan.name || "Professional" })}>
              <CreditCard className="w-4 h-4 mr-2" /> Upgrade Plan
            </Button>
          </div>
        </div>
      </div>

      {/* MODULE SUBSCRIPTION MANAGER */}
      <div className="glass p-6 rounded-lg" data-testid="module-manager">
        <h2 className="text-xl font-bold mb-4">Module Subscriptions</h2>
        {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
          const rows = grouped[cat] || [];
          if (rows.length === 0) return null;
          const Icon = CATEGORY_ICONS[cat];
          return (
            <div key={cat} className="mb-6" data-testid={`category-${cat}`}>
              <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-orange-700 font-bold">
                <Icon className="w-3.5 h-3.5" /> {label}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left pb-2">Module</th>
                      <th className="text-center pb-2">Enabled</th>
                      <th className="text-center pb-2">Units</th>
                      <th className="text-center pb-2">Usage</th>
                      <th className="text-right pb-2">Rate / Unit</th>
                      <th className="text-right pb-2">Monthly Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((m) => (
                      <tr key={m.module_key} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`module-row-${m.module_key}`}>
                        <td className="py-2.5">
                          <div className="font-semibold">{m.module_name}</div>
                          {m.is_core_module && <div className="text-[10px] text-amber-700">Core — always on</div>}
                          {m.over_limit && <div className="text-[10px] text-red-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" strokeWidth={2.5} /> Usage ({m.current_usage}) exceeds subscribed units ({m.unit_count})</div>}
                        </td>
                        <td className="text-center">
                          <Switch
                            checked={m.is_enabled}
                            disabled={m.is_core_module || busyKey === m.module_key}
                            onCheckedChange={(v) => toggleModule(m.module_key, v)}
                            data-testid={`toggle-${m.module_key}`}
                          />
                        </td>
                        <td className="text-center">
                          <Input
                            type="number" min={0}
                            value={m.unit_count}
                            onChange={(e) => updateUnits(m.module_key, e.target.value)}
                            className="w-20 text-center bg-gray-50 border-gray-200"
                            disabled={!m.is_enabled || m.is_core_module || busyKey === m.module_key}
                            data-testid={`units-${m.module_key}`}
                          />
                        </td>
                        <td className="text-center text-gray-500 text-xs">{m.current_usage}</td>
                        <td className="text-right text-gray-600">₹{m.effective_price_per_unit}</td>
                        <td className="text-right font-bold">₹{m.monthly_cost_inr.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {/* SUMMARY */}
        <div className="border-t border-gray-200 pt-4 space-y-1" data-testid="billing-summary">
          <div className="flex justify-between text-sm text-gray-500"><span>Base plan</span><span>₹{s.base_cost_monthly.toLocaleString("en-IN")}/mo</span></div>
          <div className="flex justify-between text-sm text-gray-500"><span>Modules subtotal</span><span>₹{s.modules_cost_monthly.toLocaleString("en-IN")}/mo</span></div>
          <div className="flex justify-between text-sm text-gray-500"><span>GST @18%</span><span>₹{s.gst_amount_monthly.toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between text-lg font-extrabold mt-2"><span>TOTAL {isAnnual ? "ANNUAL" : "MONTHLY"}</span>
            <span className="f360-gradient-text" data-testid="total-amount">
              ₹{(isAnnual ? s.total_annual : s.total_monthly).toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={generateInvoice} disabled={saving} className="f360-gradient border-0" data-testid="generate-invoice-btn">
            <FileText className="w-4 h-4 mr-2" strokeWidth={2.5} /> Generate Invoice
          </Button>
        </div>
      </div>

      {/* INVOICE HISTORY */}
      <div className="glass p-6 rounded-lg" data-testid="invoice-history">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><IndianRupee className="w-5 h-5 text-orange-400" strokeWidth={2.5} /> Invoice History</h2>
        {invoices.length === 0 ? (
          <div className="text-gray-500 text-sm py-6 text-center">No invoices yet. Click <b>Generate Invoice</b> above to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left pb-2">Invoice #</th>
                  <th className="text-left pb-2">Period</th>
                  <th className="text-right pb-2">Amount (incl. GST)</th>
                  <th className="text-center pb-2">Status</th>
                  <th className="text-right pb-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-t border-gray-200" data-testid={`invoice-${inv.invoice_number || inv.invoice_id}`}>
                    <td className="py-2.5 font-mono text-xs">{inv.invoice_number || "—"}</td>
                    <td className="text-gray-500 text-xs">{inv.period_start ? new Date(inv.period_start).toLocaleDateString() : "—"} – {inv.period_end ? new Date(inv.period_end).toLocaleDateString() : "—"}</td>
                    <td className="text-right font-bold">₹{(inv.total_inr ?? 0).toLocaleString("en-IN")}</td>
                    <td className="text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${inv.status === "paid" ? "bg-emerald-500/20 text-emerald-700" : inv.status === "overdue" ? "bg-red-500/20 text-red-700" : "bg-slate-500/20 text-gray-700"}`}>{inv.status || "draft"}</span>
                    </td>
                    <td className="text-right">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/invoices/${inv.invoice_id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          const tok = localStorage.getItem("f360_token");
                          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/invoices/${inv.invoice_id}/pdf`, {
                            headers: { Authorization: `Bearer ${tok}` },
                          })
                            .then((r) => r.blob())
                            .then((blob) => {
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${inv.invoice_number}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            })
                            .catch(() => toast.error("Download failed"));
                        }}
                        data-testid={`download-${inv.invoice_number}`}
                        className="text-orange-700 hover:text-gray-900 text-xs inline-flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" strokeWidth={2.5} /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CANCEL SUBSCRIPTION */}
      <div className="text-center pt-2">
        <button onClick={() => setCancelModal(true)} className="text-xs text-red-400 hover:text-red-800 underline" data-testid="cancel-sub-btn">
          Cancel Subscription
        </button>
      </div>

      {/* RAZORPAY STUB UPGRADE MODAL */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPayModal(null)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-full max-w-3xl bg-white border border-gray-200 rounded-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="payment-stub-modal">
            <h3 className="text-2xl font-bold mb-1">Upgrade Plan</h3>
            <p className="text-xs text-gray-500 mb-5">Choose the plan that best fits your fleet · All prices in INR · GST extra</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="upgrade-plan-selector">
              {plans.map((p) => {
                const selected = payModal.tier === p.plan_tier;
                const priceLabel = isAnnual ? `₹${(p.base_price_annual || 0).toLocaleString("en-IN")}/yr` : `₹${(p.base_price_monthly || 0).toLocaleString("en-IN")}/mo`;
                return (
                  <button key={p.plan_tier} onClick={() => setPayModal({ ...payModal, tier: p.plan_tier, name: p.name, amount: isAnnual ? p.base_price_annual : p.base_price_monthly })}
                    className={`text-left p-4 rounded-lg border transition-all ${selected ? "border-orange-500/60 bg-orange-500/10 ring-2 ring-orange-500/30" : "border-gray-200 bg-gray-50 hover:border-orange-500/30"}`}
                    data-testid={`upgrade-card-${p.plan_tier}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-bold text-lg">{p.name}</div>
                      {selected && <Check className="w-5 h-5 text-orange-400" strokeWidth={2.5} />}
                    </div>
                    <div className="text-xl font-extrabold f360-gradient-text">{priceLabel}</div>
                    <div className="text-xs text-gray-500 mt-2">{p.description}</div>
                  </button>
                );
              })}
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-sm text-amber-700 mb-4">
              <b>Payment gateway coming soon.</b> Our team will contact you within 24 hours to activate your upgraded plan and arrange invoicing.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPayModal(null)} className="flex-1 border-gray-200" data-testid="upgrade-close-btn">Close</Button>
              <Button className="flex-1 f360-gradient border-0" onClick={() => { toast.success(`Upgrade request to ${payModal.name} received — we'll contact you within 24 hours.`); setPayModal(null); }} data-testid="proceed-to-pay-btn">
                Proceed to Pay
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setCancelModal(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="cancel-modal">
            <h3 className="text-xl font-bold mb-2 text-red-700">Cancel Subscription?</h3>
            <p className="text-sm text-gray-500 mb-4">Your subscription will remain active until <b className="text-gray-900">{sub.subscription?.next_billing_date ? new Date(sub.subscription.next_billing_date).toLocaleDateString() : "the end of the current period"}</b>. After that, ACMS monitoring will be paused and access will be revoked.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCancelModal(false)} className="flex-1 border-gray-200">Keep Subscription</Button>
              <Button className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-700 border border-red-500/30" onClick={() => { toast.info("Cancellation request submitted. Our team will contact you."); setCancelModal(false); }} data-testid="confirm-cancel-btn">Confirm Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
